import { Avatar } from "@connected-repo/ui-mui/data-display/Avatar";
import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import type { SessionInfo } from "@frontend/contexts/UserContext";
import { useStrings } from "@frontend/strings/useStrings";
import { ButtonBase } from "@mui/material";
import { Link, useLoaderData } from "react-router";

export const userInitial = (name: string | null | undefined): string => name?.trim()[0]?.toUpperCase() ?? "?";

interface AccountMenuProps {
	/** "card" = sidebar footer (avatar + name + role); "avatar" = compact top-bar button. */
	variant: "card" | "avatar";
}

/**
 * Who is signed in. No menu: it opens the Profile page, which holds
 * appearance (light / dark / system), language, notifications and sign out.
 */
export function AccountMenu({ variant }: AccountMenuProps) {
	const { t } = useStrings();
	const user = (useLoaderData() as SessionInfo | undefined)?.user;
	const roleLabel = user?.role === "admin" ? t.roles.admin : t.roles.member;

	const avatar = (size: number) => (
		<Avatar
			src={user?.image || undefined}
			alt={user?.name}
			sx={{
				width: size,
				height: size,
				fontWeight: 800,
				fontSize: size * 0.42,
				bgcolor: "secondary.main",
				color: "secondary.contrastText",
			}}
		>
			{userInitial(user?.name)}
		</Avatar>
	);

	if (variant === "avatar") {
		return (
			<ButtonBase
				component={Link}
				to="/profile"
				aria-label={t.nav.profile}
				data-testid="account-menu"
				sx={{ borderRadius: "50%" }}
			>
				{avatar(36)}
			</ButtonBase>
		);
	}

	return (
		<ButtonBase
			component={Link}
			to="/profile"
			aria-label={t.nav.profile}
			data-testid="account-menu"
			sx={{
				width: "100%",
				justifyContent: "flex-start",
				gap: 1.25,
				p: 1,
				borderRadius: "12px",
				textAlign: "left",
				transition: "background-color 200ms",
				"&:hover": { bgcolor: "action.hover" },
			}}
		>
			{avatar(38)}
			<Box sx={{ minWidth: 0 }}>
				<Typography variant="subtitle2" noWrap>
					{user?.name}
				</Typography>
				<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
					{roleLabel}
				</Typography>
			</Box>
		</ButtonBase>
	);
}
